import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  isFilterGroup,
  FILTER_OPERATORS,
  createNestedFilterGroup,
  addConditionToGroup,
  removeConditionFromGroup,
} from "./filterUtils";

/**
 * FilterGroupUI - Visual component for nested AND/OR filter groups
 * 
 * Props:
 * - filterGroup: The root filter group { operator: 'AND'|'OR', conditions: [...] }
 * - onFilterGroupChange: Callback when the filter group changes
 * - columnsMap: Map of column metadata
 * - onAddCondition: Callback to open the condition editor
 * - depth: Current nesting depth (for styling)
 */
const FilterGroupUI = ({
  filterGroup,
  onFilterGroupChange,
  columnsMap,
  onAddCondition,
  depth = 0,
}) => {
  const [isAddingGroup, setIsAddingGroup] = useState(false);

  // Toggle between AND and OR
  const handleOperatorToggle = useCallback(() => {
    const newOperator = filterGroup.operator === 'AND' ? 'OR' : 'AND';
    onFilterGroupChange({
      ...filterGroup,
      operator: newOperator,
    });
  }, [filterGroup, onFilterGroupChange]);

  // Remove a condition or nested group by index
  const handleRemoveCondition = useCallback((index) => {
    const newGroup = removeConditionFromGroup(filterGroup, index);
    onFilterGroupChange(newGroup);
  }, [filterGroup, onFilterGroupChange]);

  // Add a new nested group
  const handleAddGroup = useCallback((operator) => {
    const newNestedGroup = createNestedFilterGroup(operator);
    const newGroup = addConditionToGroup(filterGroup, newNestedGroup);
    onFilterGroupChange(newGroup);
    setIsAddingGroup(false);
  }, [filterGroup, onFilterGroupChange]);

  // Update a nested group
  const handleNestedGroupChange = useCallback((index, updatedNestedGroup) => {
    const newConditions = [...filterGroup.conditions];
    newConditions[index] = updatedNestedGroup;
    onFilterGroupChange({
      ...filterGroup,
      conditions: newConditions,
    });
  }, [filterGroup, onFilterGroupChange]);

  // Get display label for a condition
  const getConditionLabel = (condition) => {
    const operatorLabel = FILTER_OPERATORS[condition.operator]?.label || condition.operator;
    let valueDisplay = condition.value;

    if (Array.isArray(valueDisplay)) {
      valueDisplay = `[${valueDisplay.length} items]`;
    } else if (valueDisplay && typeof valueDisplay === 'object') {
      if (valueDisplay.start !== undefined) {
        valueDisplay = `${valueDisplay.start} - ${valueDisplay.end}`;
      } else {
        valueDisplay = JSON.stringify(valueDisplay);
      }
    } else if (valueDisplay === null || valueDisplay === undefined) {
      valueDisplay = '';
    }

    // No value display for isEmpty/isNotEmpty/isNull/isNotNull
    if (['isEmpty', 'isNotEmpty', 'isNull', 'isNotNull'].includes(condition.operator)) {
      return `${condition.field} ${operatorLabel}`;
    }

    return `${condition.field} ${operatorLabel} ${valueDisplay}`;
  };

  if (!filterGroup || !filterGroup.conditions) {
    return null;
  }

  return (
    <GroupContainer depth={depth}>
      {/* Group header with operator toggle */}
      <GroupHeader depth={depth}>
        <OperatorToggle onClick={handleOperatorToggle}>
          <OperatorButton active={filterGroup.operator === 'AND'}>
            AND
          </OperatorButton>
          <OperatorButton active={filterGroup.operator === 'OR'}>
            OR
          </OperatorButton>
        </OperatorToggle>
        {depth > 0 && (
          <GroupLabel>
            {filterGroup.operator} Group
          </GroupLabel>
        )}
      </GroupHeader>

      {/* Conditions list */}
      <ConditionsList>
        {filterGroup.conditions.map((item, index) => (
          <ConditionItem key={index} depth={depth}>
            {isFilterGroup(item) ? (
              // Nested group
              <NestedGroupWrapper>
                <FilterGroupUI
                  filterGroup={item}
                  onFilterGroupChange={(updatedGroup) => handleNestedGroupChange(index, updatedGroup)}
                  columnsMap={columnsMap}
                  onAddCondition={onAddCondition}
                  depth={depth + 1}
                />
                <RemoveButton onClick={() => handleRemoveCondition(index)}>
                  <FontAwesomeIcon icon={faTimesCircle} />
                </RemoveButton>
              </NestedGroupWrapper>
            ) : (
              // Simple condition
              <ConditionRow>
                <ConditionLabel>
                  {getConditionLabel(item)}
                </ConditionLabel>
                <RemoveButton onClick={() => handleRemoveCondition(index)}>
                  <FontAwesomeIcon icon={faTimesCircle} />
                </RemoveButton>
              </ConditionRow>
            )}
            
            {/* Show connector between conditions */}
            {index < filterGroup.conditions.length - 1 && (
              <ConnectorLabel operator={filterGroup.operator}>
                {filterGroup.operator}
              </ConnectorLabel>
            )}
          </ConditionItem>
        ))}
      </ConditionsList>

      {/* Add buttons */}
      <AddButtonsRow>
        <AddButton onClick={() => onAddCondition && onAddCondition(filterGroup)}>
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
          Add Condition
        </AddButton>
        
        {depth < 2 && ( // Limit nesting to 2 levels
          isAddingGroup ? (
            <GroupTypeSelector>
              <GroupTypeButton onClick={() => handleAddGroup('AND')}>
                AND Group
              </GroupTypeButton>
              <GroupTypeButton onClick={() => handleAddGroup('OR')}>
                OR Group
              </GroupTypeButton>
              <CancelButton onClick={() => setIsAddingGroup(false)}>
                Cancel
              </CancelButton>
            </GroupTypeSelector>
          ) : (
            <AddButton onClick={() => setIsAddingGroup(true)}>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
              Add Group
            </AddButton>
          )
        )}
      </AddButtonsRow>
    </GroupContainer>
  );
};

export default FilterGroupUI;

// Styled Components
const GroupContainer = styled.div`
  padding: ${({ depth }) => depth > 0 ? '12px' : '0'};
  margin: ${({ depth }) => depth > 0 ? '8px 0' : '0'};
  border-left: ${({ depth }) => depth > 0 ? '3px solid #3899ec' : 'none'};
  background-color: ${({ depth }) => depth > 0 ? '#f8fafc' : 'transparent'};
  border-radius: ${({ depth }) => depth > 0 ? '0 4px 4px 0' : '0'};
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${({ depth }) => depth > 0 ? '8px' : '12px'};
`;

const OperatorToggle = styled.div`
  display: flex;
  border: 1px solid #dfe5eb;
  border-radius: 4px;
  overflow: hidden;
`;

const OperatorButton = styled.button`
  padding: 6px 12px;
  border: none;
  background-color: ${({ active }) => active ? '#3899ec' : 'white'};
  color: ${({ active }) => active ? 'white' : '#7a92a5'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ active }) => active ? '#3899ec' : '#eaf7ff'};
  }
`;

const GroupLabel = styled.span`
  font-size: 12px;
  color: #7a92a5;
  font-weight: 500;
`;

const ConditionsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ConditionItem = styled.div`
  position: relative;
`;

const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background-color: white;
  border: 1px solid #dfe5eb;
  border-radius: 4px;
  margin: 4px 0;
`;

const ConditionLabel = styled.span`
  font-size: 13px;
  color: #162d3d;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #cc0000;
  cursor: pointer;
  padding: 4px;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
`;

const NestedGroupWrapper = styled.div`
  position: relative;
  
  ${RemoveButton} {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const ConnectorLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  font-size: 11px;
  font-weight: 600;
  color: ${({ operator }) => operator === 'OR' ? '#e67e22' : '#3899ec'};
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${({ operator }) => operator === 'OR' ? '#fdebd0' : '#d4edfc'};
    margin: 0 8px;
  }
`;

const AddButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px dashed #3899ec;
  background-color: transparent;
  color: #3899ec;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #eaf7ff;
  }
`;

const GroupTypeSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const GroupTypeButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #3899ec;
  background-color: white;
  color: #3899ec;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #eaf7ff;
  }
`;

const CancelButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #dfe5eb;
  background-color: white;
  color: #7a92a5;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;
